import { env } from '@/env'

/**
 * Typed client for the `form-access` Edge Function — the public matchmaking
 * form's only route to the database.
 *
 * The passcode is exchanged once for a short-lived, form-scoped token. The
 * token is held in memory by the caller and deliberately not persisted: it
 * grants read access to the pick list, so it should not outlive the tab.
 */

export interface PublicFormInfo {
  slug: string
  title: string
  intro_text: string | null
  max_selections: number
  /** Whether to ask for gender at all. Answering is always optional. */
  collect_gender: boolean
  event_name: string
  organization_name: string
  logo_url: string | null
  brand_color: string | null
}

export interface PublicAttendee {
  id: string
  first_name: string
  matchmaking_id: string
}

export interface FormSession {
  token: string
  expiresAt: number
  maxSelections: number
}

export interface SubmissionPayload {
  matchmaking_id: string
  first_name: string
  last_name?: string | null
  email: string
  phone: string
  gender?: string | null
  comment?: string | null
  selections: string[]
}

export interface SubmissionReceipt {
  submission_id: string
  selections: number
  /**
   * Entries this person had already sent before this one. Submitting again adds
   * an entry rather than replacing one, so the thank-you page uses this to say
   * which of them the organiser will actually read.
   */
  previous_submissions: number
  closing_message: string | null
}

/** Stable error codes returned by the Edge Function and the SQL behind it. */
export type PublicFormErrorCode =
  | 'form_not_found'
  | 'form_closed'
  | 'form_not_open'
  | 'invalid_passcode'
  | 'rate_limited'
  | 'invalid_session'
  | 'invalid_request'
  | 'invalid_matchmaking_id'
  | 'unknown_matchmaking_id'
  | 'invalid_first_name'
  | 'invalid_email'
  | 'invalid_gender'
  | 'invalid_comment'
  | 'invalid_selections'
  | 'too_many_selections'
  | 'self_selection'
  | 'already_submitted'
  | 'unreachable'
  | 'server_error'

export class PublicFormError extends Error {
  readonly code: PublicFormErrorCode
  readonly status: number

  constructor(code: PublicFormErrorCode, status: number, message?: string) {
    super(message ?? code)
    this.name = 'PublicFormError'
    this.code = code
    this.status = status
  }
}

/** Messages safe to show an attendee: specific enough to act on, vague enough
 *  not to confirm whether a given nametag number exists to a stranger. */
const MESSAGES: Record<PublicFormErrorCode, string> = {
  form_not_found: 'This link is not valid. Please check with the event organiser.',
  form_closed: 'This form has closed. Thanks for coming!',
  form_not_open: 'This form is not open for submissions.',
  invalid_passcode: 'That passcode is not right. Please check the code from the event.',
  rate_limited: 'Too many attempts. Please wait a few minutes and try again.',
  invalid_session: 'Your session has expired. Please enter the passcode again.',
  invalid_request: 'Something in the form was not filled in correctly.',
  invalid_matchmaking_id: 'Enter the 3-digit number from your nametag.',
  unknown_matchmaking_id: 'We could not find that number. Please check your nametag.',
  invalid_first_name: 'Please enter your first name.',
  invalid_email: 'Please enter a valid email address.',
  invalid_gender: 'That answer is too long. Please shorten it.',
  invalid_comment: 'Your note is too long. Please shorten it to 1000 characters or less.',
  invalid_selections: 'One of your selections is no longer available. Please review your picks.',
  too_many_selections: 'You have selected more people than this event allows.',
  self_selection: 'You cannot select yourself.',
  already_submitted:
    'You have already submitted this form. Ask the organiser if you need to change your answers.',
  unreachable:
    'We could not reach the server. Check your connection, or ask the organiser to check the link.',
  server_error: 'Something went wrong. Please try again in a moment.',
}

export function publicFormErrorMessage(code: PublicFormErrorCode): string {
  // An unrecognised code can arrive from a newer server than this bundle.
  return (MESSAGES as Record<string, string | undefined>)[code] ?? MESSAGES.server_error
}

interface RequestOptions {
  method?: string
  body?: string
  token?: string
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Supabase's gateway requires an apikey header even for functions with
    // verify_jwt disabled. The publishable key authenticates as `anon`, which
    // has no privileges in this schema, so it grants nothing on its own.
    apikey: env.supabasePublishableKey,
  }
  if (options.token !== undefined) {
    headers.Authorization = `Bearer ${options.token}`
  }

  let response: Response
  try {
    response = await fetch(`${env.formAccessUrl}${path}`, {
      ...(options.method === undefined ? {} : { method: options.method }),
      ...(options.body === undefined ? {} : { body: options.body }),
      headers,
    })
  } catch {
    // A thrown fetch is a browser-level failure — offline, DNS, or CORS. CORS is
    // by far the most likely here and is invisible to the attendee, so leave a
    // breadcrumb for whoever is asked to look at it.
    console.error(
      `Could not reach ${env.formAccessUrl}. If the network is fine, this is usually CORS: ` +
        `the ALLOWED_ORIGINS secret on the form-access function must list ${window.location.origin} exactly.`
    )
    throw new PublicFormError('unreachable', 0, MESSAGES.unreachable)
  }

  const body: unknown = await response.json().catch(() => null)

  if (!response.ok || (body as { ok?: boolean } | null)?.ok !== true) {
    const code = ((body as { error?: string } | null)?.error ??
      'server_error') as PublicFormErrorCode
    throw new PublicFormError(code, response.status, publicFormErrorMessage(code))
  }

  return body as T
}

/** Branding for the passcode screen. Exposes nothing about attendees. */
export async function fetchFormInfo(slug: string): Promise<PublicFormInfo> {
  const body = await request<{ form: PublicFormInfo }>(`?slug=${encodeURIComponent(slug)}`)
  return body.form
}

/** Exchanges the shared event passcode for a short-lived form session. */
export async function startFormSession(slug: string, passcode: string): Promise<FormSession> {
  const body = await request<{ token: string; expires_in: number; max_selections: number }>(
    '/session',
    { method: 'POST', body: JSON.stringify({ slug, passcode }) }
  )

  return {
    token: body.token,
    expiresAt: Date.now() + body.expires_in * 1000,
    maxSelections: body.max_selections,
  }
}

/** The pick list: first name and nametag number only. */
export async function fetchFormAttendees(session: FormSession): Promise<PublicAttendee[]> {
  const body = await request<{ attendees: PublicAttendee[] }>('/attendees', {
    token: session.token,
  })
  return body.attendees
}

export async function submitForm(
  session: FormSession,
  payload: SubmissionPayload
): Promise<SubmissionReceipt> {
  return await request<SubmissionReceipt>('/submit', {
    method: 'POST',
    token: session.token,
    body: JSON.stringify(payload),
  })
}
